<script lang="ts">
  import GhostButton from "../../../../../../../../../../../_shared/_modules/ghost-button"
  import LoadingWrapper from "@kontextwerk/web-sdk/ui/layout/LoadingWrapper.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/ui/overlay/Tooltip.svelte"
  import WorkOrderFormFields from "../../../../../../../../../../_shared/_modules/work-order-form-fields"
  import type { WorkOrderFormValue } from "../../../../../../../../../../_shared/_logic/work-order-form-types"
  import { buildWorkOrderSummary } from "./_logic/workOrderSummary"
  import {
    emptyWorkOrderForm,
    normalizeDateOnly,
  } from "../../../../../../../../../../_shared/_logic/work-order-form-utils"
  import { formatDateOnly } from "../../../../../../../../../../../_shared/_logic/timezones"
  import { orderTypeHeadingLabel } from "../../../../../../../../../../../../_shared/_logic/order-type-labels"
  import {
    orderTypeSelectionFromWorkOrder,
    orderTypeSelectionPayload,
    sameOrderTypeSelection,
  } from "../../../../../../../../../../_shared/_logic/order-type-selection"
  import type { ScheduleEventCommunicationPresentation } from "../../../../../../../../../../../../api/kehrblatt/work-orders"

  type Props = {
    organizationId: string
    workOrder: WorkOrder | null
    properties?: Property[]
    customers?: Customer[]
    propertyCustomers?: PropertyCustomer[]
    contactPoints?: ContactPoint[]
    scheduleEvents?: ScheduleEvent[]
    scheduleEventCommunicationPresentations?: Record<string, ScheduleEventCommunicationPresentation>
    orderTypes?: OrderType[]
    employees?: { value: string; label: string }[]
    loading?: boolean
    error?: string | null
    saving?: boolean
    autoSave?: boolean
    onRetry?: () => void
    onSubmit?: (payload: WorkOrderUpdatePayload) => Promise<void> | void
    setSubmitFn?: (submit: () => Promise<void>) => void
    locked?: boolean
    canEdit?: boolean
  }

  const toDateInputValue = (value: string | null | undefined) => {
    return formatDateOnly(value)
  }

  const buildFormFromWorkOrder = (order: WorkOrder): WorkOrderFormValue => ({
    property_id: order.property_id ?? "",
    property_unit_id: order.property_unit_id ?? "",
    order_number: order.order_number ?? "",
    ...orderTypeSelectionFromWorkOrder(order),
    due_date: toDateInputValue(order.due_date),
    priority: order.priority !== null && order.priority !== undefined ? String(order.priority) : "",
    estimated_duration_min:
      order.estimated_duration_min !== null && order.estimated_duration_min !== undefined
        ? String(order.estimated_duration_min)
        : "",
    allowed_employee_ids: order.allowed_employee_ids ?? [],
  })

  let {
    organizationId,
    workOrder = null,
    properties = [],
    customers = [],
    propertyCustomers = [],
    contactPoints = [],
    scheduleEvents = [],
    scheduleEventCommunicationPresentations = {},
    orderTypes = [],
    employees = [],
    loading = false,
    error = null,
    saving = false,
    autoSave = true,
    onRetry = () => {},
    onSubmit = async () => {},
    setSubmitFn = () => {},
    locked = false,
    canEdit = true,
  }: Props = $props()

  let form = $derived<WorkOrderFormValue>(
    workOrder ? buildFormFromWorkOrder(workOrder) : emptyWorkOrderForm(),
  )
  let formValid = $state(false)
  let autoSavePending = $state(false)
  const formReinitKey = $derived(workOrder?.id ?? "__empty__")
  const isLocked = $derived(Boolean(locked || !canEdit))
  const summary = $derived.by(() =>
    workOrder
      ? buildWorkOrderSummary({
          workOrder,
          properties,
          customers,
          propertyCustomers,
          contactPoints,
          scheduleEvents,
          scheduleEventCommunicationPresentations,
        })
      : null,
  )

  const normalizeEmployeeIds = (value: string[] | null | undefined) =>
    [...(value ?? [])]
      .map((entry) => String(entry).trim())
      .filter(Boolean)
      .sort()

  const sameEmployeeIds = (
    left: string[] | null | undefined,
    right: string[] | null | undefined,
  ) => {
    const normalizedLeft = normalizeEmployeeIds(left)
    const normalizedRight = normalizeEmployeeIds(right)

    return (
      normalizedLeft.length === normalizedRight.length &&
      normalizedLeft.every((entry, index) => entry === normalizedRight[index])
    )
  }

  /** Sends the order type selection only when it differs, since it replaces all stored types. */
  const buildUpdatePayload = (data: WorkOrderFormValue): WorkOrderUpdatePayload => {
    const orderTypeSelectionChanged =
      !workOrder || !sameOrderTypeSelection(orderTypeSelectionFromWorkOrder(workOrder), data)

    return {
      ...(orderTypeSelectionChanged ? orderTypeSelectionPayload(data) : {}),
      due_date: normalizeDateOnly(data.due_date),
      allowed_employee_ids: data.allowed_employee_ids?.length ? data.allowed_employee_ids : null,
    }
  }

  const hasPersistedChange = (payload: WorkOrderUpdatePayload) => {
    if (!workOrder) return false

    const persistedDueDate = normalizeDateOnly(toDateInputValue(workOrder.due_date))
    const nextDueDate = normalizeDateOnly(String(payload.due_date ?? ""))
    const changesOrderTypes =
      payload.order_type_ids !== undefined || payload.custom_order_type !== undefined

    return (
      persistedDueDate !== nextDueDate ||
      changesOrderTypes ||
      !sameEmployeeIds(workOrder.allowed_employee_ids ?? [], payload.allowed_employee_ids ?? [])
    )
  }

  const autoSaveDetails = async (data: WorkOrderFormValue) => {
    if (!workOrder || !onSubmit || isLocked || loading || saving || autoSavePending) return

    const payload = buildUpdatePayload(data)

    if (!hasPersistedChange(payload)) return

    autoSavePending = true
    try {
      await onSubmit(payload)
    } finally {
      autoSavePending = false
    }
  }

  const submitDetails = async (data: WorkOrderFormValue) => {
    if (!workOrder || !onSubmit || isLocked || loading || saving) return

    const payload = buildUpdatePayload(data)

    if (!hasPersistedChange(payload)) return

    await onSubmit(payload)
  }

  const handleFormChange = (payload: { data: WorkOrderFormValue; valid: boolean }) => {
    form = {
      ...payload.data,
      allowed_employee_ids: [...(payload.data.allowed_employee_ids ?? [])],
    }
    formValid = payload.valid

    if (autoSave) {
      void autoSaveDetails(form)
    }
  }

  const formDisabled = $derived(Boolean(isLocked || saving || loading || autoSavePending))
  const employeeLabelById = $derived(
    new Map(employees.map((employee) => [employee.value, employee.label])),
  )
  const allowedEmployeeSummary = $derived.by(() => {
    const selected = workOrder?.allowed_employee_ids ?? []

    if (!selected.length) return "Alle Mitarbeiter"

    return selected
      .map((id) => employeeLabelById.get(id) ?? `Mitarbeiter ${id.slice(0, 8)}`)
      .join(", ")
  })
  const planningFacts = $derived([
    {
      label: "Gesetzliche Frist",
      value:
        summary?.facts.find((fact) => fact.label === "Gesetzliche Frist")?.value ??
        "Nicht hinterlegt",
    },
    { label: "Zugelassene Mitarbeiter", value: allowedEmployeeSummary },
  ])
  const withoutAppointmentDate = (value: string) => {
    const separatorIndex = value.indexOf(" · ")

    return separatorIndex === -1 ? value : value.slice(0, separatorIndex)
  }
  const compactAppointmentDate = (value: string) => {
    const separatorIndex = value.indexOf(" · ")

    if (separatorIndex === -1) return "Nicht geplant"

    const dateTime = value.slice(separatorIndex + 3)
    const commaIndex = dateTime.indexOf(",")

    if (commaIndex === -1) return dateTime

    const date = dateTime.slice(0, commaIndex)
    const time = dateTime.slice(commaIndex + 1).trim()

    return `${date.slice(0, 6)} · ${time} Uhr`
  }
  const orderHeadingFacts = $derived(
    summary?.orderFacts
      .filter(
        (fact) =>
          fact.label === "Auftragsnummer" ||
          fact.label === "Auftragstyp" ||
          fact.label === "Auftragstypen",
      )
      .map((fact) => ({
        ...fact,
        label:
          fact.label === "Auftragsnummer" ? "Nummer" : orderTypeHeadingLabel(workOrder, "Typ"),
      })) ?? [],
  )
  const statusFacts = $derived.by(() => {
    const appointmentFact = summary?.orderFacts.find((fact) => fact.label === "Terminlage")

    if (!appointmentFact) return []

    return [
      { ...appointmentFact, value: withoutAppointmentDate(appointmentFact.value) },
      { label: "Termin", value: compactAppointmentDate(appointmentFact.value) },
    ]
  })
</script>

{#if loading}
  <LoadingWrapper loading={true} />
{:else if error}
  <div class="state-block">
    <p>{error}</p>
    <GhostButton onclick={onRetry}>Erneut versuchen</GhostButton>
  </div>
{:else if workOrder}
  <div class="details-stack" data-testid="work-order-details">
    <div class="details-primary-grid">
      <section class="details-section" aria-labelledby="work-order-overview-heading">
        <header class="details-section__header">
          <h3 id="work-order-overview-heading">Auftrag</h3>
        </header>
        <dl class="summary-grid summary-grid--order">
          {#each orderHeadingFacts as fact}
            <div class="summary-item">
              <dt class="summary-label">{fact.label}</dt>
              <dd class="summary-value">{fact.value}</dd>
            </div>
          {/each}
        </dl>
      </section>

      <section class="details-section" aria-labelledby="work-order-location-heading">
        <header class="details-section__header">
          <h3 id="work-order-location-heading">Ort</h3>
        </header>
        <dl class="summary-grid">
          {#each summary?.locationFacts ?? [] as fact}
            <div class="summary-item">
              <dt class="summary-label">{fact.label}</dt>
              <dd class="summary-value">{fact.value}</dd>
            </div>
          {/each}
        </dl>
      </section>

      <section class="details-section" aria-labelledby="work-order-status-heading">
        <header class="details-section__header">
          <h3 id="work-order-status-heading">Status</h3>
        </header>
        <dl class="summary-grid summary-grid--status">
          {#each statusFacts as fact}
            <div class="summary-item">
              <dt class="summary-label">{fact.label}</dt>
              <dd class="summary-value">{fact.value}</dd>
            </div>
          {/each}
        </dl>
      </section>
    </div>

    <section
      class="details-section details-section--full"
      aria-labelledby="work-order-planning-heading"
    >
      <header class="details-section__header">
        <h3 id="work-order-planning-heading">Planung</h3>
      </header>
      {#if isLocked}
        <dl class="summary-grid summary-grid--compact">
          {#each planningFacts as fact}
            <div class="summary-item">
              <dt class="summary-label">{fact.label}</dt>
              <dd class="summary-value">{fact.value}</dd>
            </div>
          {/each}
        </dl>
      {:else}
        <div class="planning-form">
          {#key formReinitKey}
            <WorkOrderFormFields
              value={form}
              valid={formValid}
              {organizationId}
              {properties}
              {customers}
              {orderTypes}
              {employees}
              disabled={formDisabled}
              brightBackground={true}
              showPropertySelect={false}
              showOrderNumber={false}
              showOrderTypeFields={false}
              showReset={false}
              mode="edit"
              dueDateDisabled={false}
              allowedEmployeesDisabled={false}
              showSubmit={false}
              onChange={handleFormChange}
              onSubmit={submitDetails}
              {setSubmitFn}
            ></WorkOrderFormFields>
          {/key}
        </div>
      {/if}
    </section>

    <section
      class="details-section details-section--full"
      aria-labelledby="work-order-contacts-heading"
    >
      <header class="details-section__header">
        <h3 id="work-order-contacts-heading">Ansprechpartner</h3>
      </header>
      {#if summary?.contactRows.length}
        <div class="contact-list">
          {#each summary.contactRows as contact}
            <article class="contact-card">
              <div class="contact-card__heading">
                <span class="contact-name">{contact.label}</span>
                <span class="role-chip">{contact.roleLabel}</span>
              </div>
              <div class="contact-card__channels" aria-label={`Kontaktwege von ${contact.label}`}>
                {#each contact.contactChannels as channel}
                  <span
                    class="contact-chip"
                    title={channel.tooltip}
                    use:tooltip={{ content: channel.tooltip }}
                  >
                    {channel.tooltip}
                  </span>
                {:else}
                  <span class="contact-empty">Keine Kontaktinformationen</span>
                {/each}
              </div>
            </article>
          {/each}
        </div>
      {:else}
        <p class="empty-copy">Keine Ansprechpartner zugeordnet</p>
      {/if}
    </section>
  </div>
{/if}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .state-block {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }

  .details-stack {
    display: flex;
    flex-direction: column;
  }

  .details-stack {
    --work-order-summary-text: var(--text-invers-100);
    --work-order-summary-muted: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
    gap: 1rem;
    color: var(--text-invers-100);
  }

  .details-primary-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.7fr);
    gap: 1rem;
  }

  .details-section {
    min-width: 0;
    border: 1px solid color-mix(in srgb, var(--work-order-summary-text) 16%, transparent);
    border-left: 4px solid color-mix(in srgb, var(--primary-100) 78%, transparent);
    padding: 1rem 1.1rem 1.1rem;
  }

  .details-section__header {
    margin-bottom: 1rem;
    border-bottom: 1px solid color-mix(in srgb, var(--work-order-summary-text) 13%, transparent);
    padding-bottom: 0.55rem;

    h3 {
      margin: 0;
      color: var(--work-order-summary-text);
      font-size: 0.9rem;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem 1rem;
    margin: 0;

    &--compact {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }

    &--order {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &--status {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }

  .summary-label {
    color: var(--work-order-summary-muted);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .summary-value {
    margin: 0;
    color: var(--work-order-summary-text);
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .planning-form {
    color: var(--work-order-summary-text);
  }

  .contact-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.75rem;
  }

  .contact-card {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.85rem;
    border: 1px solid color-mix(in srgb, var(--work-order-summary-text) 14%, transparent);
    background: color-mix(in srgb, var(--work-order-summary-text) 4%, transparent);
    padding: 0.9rem;
  }

  .contact-card__heading,
  .contact-card__channels {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-wrap: wrap;
    min-width: 0;
  }

  .contact-card__heading {
    justify-content: space-between;
  }

  .contact-name {
    color: var(--work-order-summary-text);
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .role-chip,
  .contact-chip {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    border: 1px solid var(--work-order-summary-text);
    padding: 0.12rem 0.45rem;
    color: var(--work-order-summary-text);
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .role-chip {
    border-color: var(--work-order-summary-text);
    background: transparent;
  }

  .contact-empty,
  .empty-copy {
    color: var(--work-order-summary-muted);
    margin: 0;
  }

  @media @mobile {
    .details-primary-grid,
    .contact-list,
    .summary-grid--order,
    .summary-grid--status {
      grid-template-columns: 1fr;
    }

    .details-section {
      padding: 0.85rem;
    }

    .contact-card__heading {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>
